import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Select,
  Space,
  message,
  Tag,
  Row,
  Col,
  Typography,
  Tooltip,
  Drawer,
  Input,
  DatePicker,
  Divider,
  Alert,
  Spin,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  LinkOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import {
  getOfflineCollections,
  getOfflineCollection,
  matchTransaction,
} from '../services/offlineCollectionService';
import { OfflineCollectionSchema, MatchingTransactions } from '../types/offline-collections';
import { formatDate } from '../utils/format';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const OfflineCollections: React.FC = () => {
  const [collections, setCollections] = useState<OfflineCollectionSchema[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [matchModalVisible, setMatchModalVisible] = useState(false);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<OfflineCollectionSchema | null>(null);
  const [matchingTransactions, setMatchingTransactions] = useState<MatchingTransactions[]>([]);
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({
    status: '',
    currency: '',
    amount: '',
    dateRange: null as any,
  });
  const observerTarget = useRef<HTMLDivElement>(null);
  const LIMIT = 15;

  // Helper function to format number with commas
  const formatNumberWithCommas = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const fetchCollections = useCallback(async (searchFilters = filters, reset = true) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const queryParams: any = {
        limit: LIMIT,
      };
      
      if (searchFilters.status) queryParams.status = searchFilters.status;
      if (searchFilters.currency) queryParams.currency = searchFilters.currency;
      if (searchFilters.amount) queryParams.amount = searchFilters.amount.replace(/,/g, ''); // Remove commas for API call
      if (searchFilters.dateRange && searchFilters.dateRange.length === 2) {
        queryParams.from = searchFilters.dateRange[0].valueOf();
        queryParams.to = searchFilters.dateRange[1].valueOf();
      }
      
      // Add cursor for pagination
      if (!reset && nextCursor) {
        queryParams.next_cursor = nextCursor;
      }

      const response = await getOfflineCollections(queryParams);
      const newCollections = response.collections || [];
      
      if (reset) {
        setCollections(newCollections);
      } else {
        setCollections(prev => [...prev, ...newCollections]);
      }
      
      setNextCursor(response.next_cursor || null);
      setHasMore(!!response.next_cursor);
    } catch (error) {
      console.error('Error fetching offline collections:', error);
      message.error('Failed to fetch offline collections');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [nextCursor, LIMIT]);

  // Load initial data
  useEffect(() => {
    fetchCollections();
  }, []);

  // Set up intersection observer for infinite scrolling
  useEffect(() => {
    if (!observerTarget.current || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !loading && !loadingMore) {
          fetchCollections(filters, false);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(observerTarget.current);

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore, fetchCollections, filters]);

  const handleViewDetails = async (collection: OfflineCollectionSchema) => {
    try {
      const detailedCollection = await getOfflineCollection(collection.id, { include_matching_txns: 'true' });
      setSelectedCollection(detailedCollection);
      setMatchingTransactions(detailedCollection.matching_transactions || []);
      setDetailDrawerVisible(true);
    } catch (error) {
      console.error('Error fetching collection details:', error);
      message.error('Failed to fetch collection details');
    }
  };

  const handleMatchTransaction = (collection: OfflineCollectionSchema) => {
    setSelectedCollection(collection);
    setMatchModalVisible(true);
    form.resetFields();
  };

  const handleMatchSubmit = async (values: { transaction_id: string }) => {
    if (!selectedCollection) return;

    try {
      await matchTransaction(selectedCollection.id, {
        action: 'match-transaction',
        transaction_id: values.transaction_id,
      });
      message.success('Transaction matched successfully');
      setMatchModalVisible(false);
      form.resetFields();
      // Refresh the collections list to show changes
      setNextCursor(null);
      setHasMore(true);
      fetchCollections(filters, true);
    } catch (error) {
      console.error('Error matching transaction:', error);
      message.error('Failed to match transaction');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'matched':
        return 'green';
      case 'pending':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'matched':
        return <CheckCircleOutlined />;
      case 'pending':
        return <ClockCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const columns = [
    {
      title: 'Sender Email',
      dataIndex: 'alert_sender',
      key: 'alert_sender',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 120,
      render: (_, record: OfflineCollectionSchema) => (
        <Text strong>
          {record.currency} {record.amount.toLocaleString()}
        </Text>
      ),
    },
    {
      title: 'Narration',
      dataIndex: 'narration',
      key: 'narration',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Alert Time',
      dataIndex: 'alert_timestamp',
      key: 'alert_timestamp',
      width: 120,
      render: (timestamp: number) => {
        if (!timestamp) return 'N/A';
        // Convert to milliseconds if it's in seconds
        const date = new Date(timestamp > 1e10 ? timestamp : timestamp * 1000);
        return date.toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: '2-digit',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }).replace(',', '');
      },
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: 120,
      render: (timestamp: number) => {
        if (!timestamp) return 'N/A';
        // Convert to milliseconds if it's in seconds
        const date = new Date(timestamp > 1e10 ? timestamp : timestamp * 1000);
        return date.toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: '2-digit',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }).replace(',', '');
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record: OfflineCollectionSchema) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          {record.status.toLowerCase() === 'pending' && (
            <Tooltip title="Match Transaction">
              <Button
                type="text"
                icon={<LinkOutlined />}
                onClick={() => handleMatchTransaction(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>

        <Card title="Filters" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Filter by Status</label>
                <Select
                  placeholder="Select status"
                  value={filters.status}
                  onChange={(value) => setFilters({ ...filters, status: value })}
                  allowClear
                  style={{ width: '100%' }}
                >
                  <Option value="pending">Pending</Option>
                  <Option value="matched">Matched</Option>
                </Select>
              </div>
            </Col>
            <Col span={6}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Filter by Currency</label>
                <Select
                  placeholder="Select currency"
                  value={filters.currency}
                  onChange={(value) => setFilters({ ...filters, currency: value })}
                  allowClear
                  style={{ width: '100%' }}
                >
                  <Option value="NGN">NGN</Option>
                  <Option value="USD">USD</Option>
                  <Option value="EUR">EUR</Option>
                  <Option value="GBP">GBP</Option>
                </Select>
              </div>
            </Col>
            <Col span={6}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Filter by Amount</label>
                <Input
                  placeholder="Enter amount (e.g., 1,000)"
                  value={filters.amount}
                  onChange={(e) => {
                    const formattedValue = formatNumberWithCommas(e.target.value);
                    setFilters({ ...filters, amount: formattedValue });
                  }}
                  onKeyDown={(e) => {
                    // Allow: backspace, delete, tab, escape, enter, decimal point
                    if ([8, 9, 27, 13, 46, 110, 190].indexOf(e.keyCode) !== -1 ||
                        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                        (e.keyCode === 65 && e.ctrlKey === true) ||
                        (e.keyCode === 67 && e.ctrlKey === true) ||
                        (e.keyCode === 86 && e.ctrlKey === true) ||
                        (e.keyCode === 88 && e.ctrlKey === true) ||
                        // Allow: home, end, left, right, down, up
                        (e.keyCode >= 35 && e.keyCode <= 40)) {
                      return;
                    }
                    // Ensure that it is a number and stop the keypress
                    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
            </Col>
            <Col span={6}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Filter by Date Range</label>
                <RangePicker
                  value={filters.dateRange}
                  onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
                  style={{ width: '100%' }}
                  disabledDate={(current) => {
                    // Disable future dates
                    return current && current > dayjs().endOf('day');
                  }}
                />
              </div>
            </Col>
          </Row>
          <div style={{ marginTop: 16 }}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => {
                setNextCursor(null);
                setHasMore(true);
                fetchCollections(filters, true);
              }}
            >
              Search
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => {
                setFilters({ status: '', currency: '', amount: '', dateRange: null });
                setNextCursor(null);
                setHasMore(true);
                fetchCollections({ status: '', currency: '', amount: '', dateRange: null }, true);
              }}
            >
              Clear
            </Button>
          </div>
        </Card>

        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>
            Offline Collections
          </Title>
        </div>

        <Table
          columns={columns}
          dataSource={collections}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1000 }}
        />
        
        {/* Infinite scroll target */}
        <div ref={observerTarget} style={{ height: '20px', margin: '20px 0' }}>
          {loadingMore && (
            <div style={{ textAlign: 'center' }}>
              <Spin size="small" />
              <span style={{ marginLeft: 8 }}>Loading more collections...</span>
            </div>
          )}
        </div>
      </Card>

      <Modal
        title="Match Transaction"
        open={matchModalVisible}
        onCancel={() => {
          setMatchModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        {selectedCollection && (
          <div style={{ marginBottom: 16 }}>
            <Alert
              message="Collection Details"
              description={
                <div>
                  <p><strong>Sender Email:</strong> {selectedCollection.alert_sender}</p>
                  <p><strong>Amount:</strong> {selectedCollection.currency} {selectedCollection.amount.toLocaleString()}</p>
                  <p><strong>Narration:</strong> {selectedCollection.narration}</p>
                  {selectedCollection.provider_reference && (
                    <p><strong>Provider Reference:</strong> {selectedCollection.provider_reference}</p>
                  )}
                </div>
              }
              type="info"
            />
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleMatchSubmit}
        >
          <Form.Item
            name="transaction_id"
            label="Transaction ID"
            rules={[{ required: true, message: 'Please enter transaction ID' }]}
          >
            <Input placeholder="Enter transaction ID to match" />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title="Collection Details"
        placement="right"
        width={600}
        open={detailDrawerVisible}
        onClose={() => setDetailDrawerVisible(false)}
      >
        {selectedCollection && (
          <div>
            <Card title="Collection Information" style={{ marginBottom: 16 }}>
              <p><strong>Sender Email:</strong> {selectedCollection.alert_sender}</p>
              <p><strong>Amount:</strong> <Text strong>{selectedCollection.currency} {selectedCollection.amount.toLocaleString()}</Text></p>
              <p><strong>Narration:</strong> {selectedCollection.narration}</p>
              {selectedCollection.provider_reference && (
                <p><strong>Provider Reference:</strong> {selectedCollection.provider_reference}</p>
              )}
              <p><strong>Status:</strong> <Tag color={getStatusColor(selectedCollection.status)} icon={getStatusIcon(selectedCollection.status)}>{selectedCollection.status.toUpperCase()}</Tag></p>
              <p><strong>Alert Time:</strong> {
                selectedCollection.alert_timestamp ? 
                  new Date(selectedCollection.alert_timestamp > 1e10 ? selectedCollection.alert_timestamp : selectedCollection.alert_timestamp * 1000)
                    .toLocaleString('en-US', {
                      month: '2-digit',
                      day: '2-digit',
                      year: '2-digit',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    }).replace(',', '') : 'N/A'
              }</p>
              <p><strong>Created:</strong> {
                selectedCollection.created ? 
                  new Date(selectedCollection.created > 1e10 ? selectedCollection.created : selectedCollection.created * 1000)
                    .toLocaleString('en-US', {
                      month: '2-digit',
                      day: '2-digit',
                      year: '2-digit',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    }).replace(',', '') : 'N/A'
              }</p>
            </Card>

            {matchingTransactions.length > 0 && (
              <Card title="Matching Transactions">
                {matchingTransactions.map((txn, index) => (
                  <div key={index} style={{ marginBottom: 16, padding: 12, border: '1px solid #f0f0f0', borderRadius: 6 }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <p><strong>Transaction ID:</strong> {txn.transaction_id}</p>
                        <p><strong>Amount:</strong> {txn.currency} {txn.amount.toLocaleString()}</p>
                      </Col>
                      <Col span={12}>
                        <p><strong>Customer:</strong> {txn.user_name}</p>
                        <p><strong>Created:</strong> {
                          txn.created ? 
                            new Date(txn.created > 1e10 ? txn.created : txn.created * 1000)
                              .toLocaleString('en-US', {
                                month: '2-digit',
                                day: '2-digit',
                                year: '2-digit',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              }).replace(',', '') : 'N/A'
                        }</p>
                      </Col>
                    </Row>
                  </div>
                ))}
              </Card>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default OfflineCollections;
